<template>
  <div
    class="imusic-mesh-background"
    :class="variant"
    aria-hidden="true"
  >
    <div
      class="imusic-mesh-gradient-layer"
      :class="{ 'is-visible': activeLayerIndex === 0 }"
      :style="layerStyles[0]"
    />

    <div
      class="imusic-mesh-gradient-layer"
      :class="{ 'is-visible': activeLayerIndex === 1 }"
      :style="layerStyles[1]"
    />

    <transition name="photo-blur-fade">
      <div
        v-if="photoBackgroundUrl"
        class="imusic-photo-blur-layer"
        :style="{ backgroundImage: `url(${photoBackgroundUrl})` }"
      >
        <div class="imusic-photo-tint" />
      </div>
    </transition>

    <div class="imusic-mesh-atmosphere" />
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import {
  buildMeshBackgroundStyle,
  extractColorsFromImageUrl,
  getThemeFallbackColors
} from '@utils/imusicBackgroundUtils.js';
import pinkDefaultBg from '@/assets/images/background/BGPIC.jpg';
import pinkDefaultBgPc from '@/assets/images/background/BGPIC-PC.jpg';
import pinkDefaultBlurBg from '@/assets/images/background/default_blur_bgd.jpg';

const props = defineProps({
  sourceImageUrl: {
    type: String,
    default: ''
  },
  themeMode: {
    type: String,
    default: 'pink'
  },
  variant: {
    type: String,
    default: 'page',
    validator: (value) => ['page', 'player'].includes(value)
  }
});

const emptyLayerStyle = {
  backgroundColor: 'rgb(168, 176, 188)',
  backgroundImage: 'none'
};

const layerStyles = ref([{ ...emptyLayerStyle }, { ...emptyLayerStyle }]);
const activeLayerIndex = ref(0);
const extractionToken = ref(0);
const hasDisplayedBackground = ref(false);
const lastAppliedSourceUrl = ref('');

const fallbackColors = computed(() => getThemeFallbackColors(props.themeMode));

// 粉色主题的默认图已预模糊，直接铺图，不再取色生成弥散背景
const PHOTO_SOURCES_BY_THEME = {
  pink: new Set([pinkDefaultBg, pinkDefaultBgPc, pinkDefaultBlurBg])
};

const photoBackgroundUrl = computed(() => {
  const photoSources = PHOTO_SOURCES_BY_THEME[props.themeMode];
  if (!photoSources) return '';
  return photoSources.has(props.sourceImageUrl) ? props.sourceImageUrl : '';
});

function toLayerStyle(colors) {
  const style = buildMeshBackgroundStyle(colors);

  return {
    backgroundColor: style.backgroundColor,
    backgroundImage: style.backgroundImage
  };
}

function waitForPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });
}

async function showBackground(colors) {
  const layerStyle = toLayerStyle(colors);

  if (!hasDisplayedBackground.value) {
    layerStyles.value[0] = layerStyle;
    layerStyles.value[1] = { ...layerStyle };
    activeLayerIndex.value = 0;
    hasDisplayedBackground.value = true;
    return;
  }

  const nextIndex = activeLayerIndex.value === 0 ? 1 : 0;
  layerStyles.value[nextIndex] = layerStyle;

  await nextTick();
  await waitForPaint();
  activeLayerIndex.value = nextIndex;
}

async function applyColorsFromUrl(url) {
  const normalizedUrl = url || '';

  if (normalizedUrl === lastAppliedSourceUrl.value) {
    return;
  }

  const token = ++extractionToken.value;

  if (!normalizedUrl) {
    if (token !== extractionToken.value) return;
    await showBackground(fallbackColors.value);
    lastAppliedSourceUrl.value = '';
    return;
  }

  const colors = await extractColorsFromImageUrl(normalizedUrl, { themeMode: props.themeMode });
  if (token !== extractionToken.value) return;

  await showBackground(colors);
  lastAppliedSourceUrl.value = normalizedUrl;
}

watch(
  () => [props.sourceImageUrl, props.themeMode],
  ([url, themeMode], oldValue = []) => {
    const [, oldThemeMode] = oldValue;
    if (themeMode !== oldThemeMode) {
      lastAppliedSourceUrl.value = '';
    }

    if (photoBackgroundUrl.value) {
      return;
    }

    applyColorsFromUrl(url);
  },
  { immediate: true }
);
</script>

<style scoped>
.imusic-mesh-background {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: calc(var(--vh, 1vh) * 100);
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
  background: #a8b0bc;
}

.imusic-mesh-background.player {
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.imusic-mesh-gradient-layer {
  position: absolute;
  inset: -12%;
  width: 124%;
  height: 124%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  opacity: 0;
  filter: blur(clamp(24px, 6vmin, 48px)) saturate(1.16);
  -webkit-filter: blur(clamp(24px, 6vmin, 48px)) saturate(1.16);
  transition: opacity 460ms ease;
  will-change: opacity, filter;
  transform: translateZ(0);
}

.imusic-mesh-gradient-layer.is-visible {
  opacity: 1;
}

/* 粉色主题：预模糊照片直接铺满，运行时零模糊计算 */
.imusic-photo-blur-layer {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  transform: translateZ(0);
  will-change: opacity;
}

/* 照片背景 ↔ 弥散背景切换时交叉淡入淡出 */
.photo-blur-fade-enter-active,
.photo-blur-fade-leave-active {
  transition: opacity 480ms ease;
}

.photo-blur-fade-enter-from,
.photo-blur-fade-leave-to {
  opacity: 0;
}

.imusic-photo-tint {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: transparent;
}

.imusic-mesh-atmosphere {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(ellipse 130% 100% at 50% 40%, rgba(255, 255, 255, 0.01) 0%, transparent 52%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.006) 0%, transparent 38%, rgba(0, 0, 0, 0.012) 100%);
}

.imusic-mesh-background.player .imusic-mesh-gradient-layer {
  inset: -14%;
  width: 128%;
  height: 128%;
  filter: blur(clamp(28px, 7vmin, 52px)) saturate(1.16);
  -webkit-filter: blur(clamp(28px, 7vmin, 52px)) saturate(1.16);
}

@media (max-width: 768px) {
  .imusic-mesh-background.page .imusic-mesh-gradient-layer {
    filter: blur(clamp(20px, 5.5vmin, 42px)) saturate(1.16);
    -webkit-filter: blur(clamp(20px, 5.5vmin, 42px)) saturate(1.16);
  }
}

@media (prefers-reduced-motion: reduce) {
  .imusic-mesh-gradient-layer {
    transition: none !important;
  }
}
</style>
